import { Navigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface ProtectedRouteProps {
  children: React.ReactNode;
}

export const ProtectedRoute = ({ children }: ProtectedRouteProps) => {
  const { user, loading } = useAuth();

  // Auto-refresh token when session is about to expire
  useEffect(() => {
    if (!user) return;

    const refreshToken = async () => {
      try {
        const { data: { session }, error } = await supabase.auth.getSession();
        
        if (error) throw error;
        
        if (session) {
          // Check if token is about to expire (within 5 minutes)
          const expiresAt = session.expires_at ? session.expires_at * 1000 : 0;
          const now = Date.now();
          const timeUntilExpiry = expiresAt - now;
          const fiveMinutes = 5 * 60 * 1000;

          if (timeUntilExpiry < fiveMinutes && timeUntilExpiry > 0) {
            await supabase.auth.refreshSession();
          }
        }
      } catch (error) {
        console.error('Error refreshing session:', error);
      }
    };

    // Check token on mount
    refreshToken();

    // Set up interval to check every minute
    const interval = setInterval(refreshToken, 60 * 1000);

    return () => clearInterval(interval);
  }, [user]);

  // Show loading spinner while checking authentication
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary" />
      </div>
    );
  }

  // Redirect to home if not authenticated
  if (!user) {
    return <Navigate to="/" replace />;
  }

  // Render the protected content
  return <>{children}</>;
};
